import React from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import loadingLogo from "../../../assets/emlogo_loading_transparent.gif";
import { useInsightsWs } from "../Utils/CoTraderUtils";
import {
  formatDateTimeWithZone,
  formatTimeWithZone,
  parseUtcTimestampToMs,
  TimestampInput,
} from "../Utils/timezone";
import { useDisplayTimeZone } from "../Utils/displayTimeZone";
import { useTimeSince } from "../Utils/relativeTime";
import { useResponsiveTileTypography } from "../Utils/useResponsiveTileTypography";

interface CoTraderInsightsComponentProps {
  currencyPair: string;
  updateSpeed: number;
  forward_tile: boolean;
  variant?: "default" | "preTrade";
  streamedInsights?: any[];
  streamedLoading?: boolean;
  useStreamData?: boolean;
}

const clampImpactScore = (value: unknown) => {
  if (value == null || value === "") return null;

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return null;

  return Math.max(-1, Math.min(1, parsedValue));
};

const getImpactTone = (impactScore: unknown) => {
  const clampedScore = clampImpactScore(impactScore);
  if (clampedScore == null) return null;

  const hue = ((clampedScore + 1) / 2) * 120;
  const label =
    clampedScore > 0.12
      ? "Supportive"
      : clampedScore < -0.12
        ? "Negative"
        : "Neutral";

  return {
    background: `linear-gradient(180deg, hsla(${hue}, 88%, 56%, 0.14) 0%, rgba(255,255,255,0.96) 100%)`,
    border: `hsla(${hue}, 84%, 42%, 0.28)`,
    accent: `hsl(${hue}, 84%, 38%)`,
    badgeBg: `hsla(${hue}, 88%, 50%, 0.12)`,
    badgeBorder: `hsla(${hue}, 84%, 38%, 0.22)`,
    label,
    scoreText: `${clampedScore > 0 ? "+" : ""}${clampedScore.toFixed(2)}`,
  };
};

const getPreTradeSentimentTone = (label: string) => {
  switch (label) {
    case "Supportive":
      return {
        accent: "#71C98E",
        surface: "rgba(73, 158, 104, 0.1)",
      };
    case "Negative":
      return {
        accent: "#E68A8A",
        surface: "rgba(185, 83, 83, 0.1)",
      };
    default:
      return {
        accent: "#AEBFD1",
        surface: "rgba(133, 155, 181, 0.1)",
      };
  }
};

const getPreTradeImpactLabel = (label: string) => {
  switch (label) {
    case "Supportive":
      return "Supportive impact";
    case "Negative":
      return "Bearish risk";
    default:
      return "Neutral impact";
  }
};

type InsightIconKind = "up" | "down" | "bars" | "clock" | "calendar";

const InsightIcon: React.FunctionComponent<{
  kind: InsightIconKind;
  color: string;
}> = ({ kind, color }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    {kind === "up" ? (
      <>
        <polyline points="3 17 9 11 13 15 21 7" />
        <polyline points="15 7 21 7 21 13" />
      </>
    ) : kind === "down" ? (
      <>
        <polyline points="3 7 9 13 13 9 21 17" />
        <polyline points="15 17 21 17 21 11" />
      </>
    ) : kind === "clock" ? (
      <>
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
      </>
    ) : kind === "calendar" ? (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <line x1="16" y1="3" x2="16" y2="7" />
        <line x1="8" y1="3" x2="8" y2="7" />
        <line x1="3" y1="11" x2="21" y2="11" />
      </>
    ) : (
      <>
        <line x1="4" y1="20" x2="20" y2="20" />
        <line x1="7" y1="16" x2="7" y2="12" />
        <line x1="12" y1="16" x2="12" y2="6" />
        <line x1="17" y1="16" x2="17" y2="9" />
      </>
    )}
  </svg>
);

// Isolated so the shared relative-time clock re-renders only this label, never
// the whole feed. The absolute arrival time stays available on hover.
const TimeSince = React.memo<{ timestamp: TimestampInput; timeZone: string }>(
  ({ timestamp, timeZone }) => {
    const label = useTimeSince(timestamp);

    return (
      <span title={formatTimeWithZone(timestamp, timeZone)}>
        {label}
      </span>
    );
  }
);

// New insights are prepended, so index keys would hand the top card's
// component (and its label state) to a different insight on every update.
// The release timestamp disambiguates any id the server repeats.
const getInsightKey = (insight: any, index: number) =>
  insight?.id != null ? `${insight.id}-${insight.timestamp}` : `idx-${index}`;

const isUpcomingReleaseTitle = (title: unknown) => {
  const normalizedTitle = String(title || "").toLocaleLowerCase();

  return /\bupcoming\b|\bscheduled\b|\bto be released\b|\beconomic data releases?\b/.test(
    normalizedTitle
  );
};

const CoTraderInsightsComponent: React.FunctionComponent<
  CoTraderInsightsComponentProps
> = ({
  currencyPair,
  updateSpeed,
  forward_tile,
  variant = "default",
  streamedInsights,
  streamedLoading,
  useStreamData = false,
}) => {
  const theme = useTheme();
  const { timeZone } = useDisplayTimeZone();
  const isPreTrade = variant === "preTrade";
  const [currentTimeMs, setCurrentTimeMs] = React.useState(() => Date.now());
  const { containerRef, tier } =
    useResponsiveTileTypography<HTMLDivElement>();

  React.useEffect(() => {
    const timer = window.setInterval(() => setCurrentTimeMs(Date.now()), 30000);

    return () => window.clearInterval(timer);
  }, []);

  const typography = React.useMemo(() => {
    switch (tier) {
      case "compact":
        return {
          containerPadding: "8px",
          cardPadding: "10px",
          cardGap: "8px",
          cardMarginBottom: "8px",
          titleSize: "14px",
          descriptionSize: "12px",
          sourceSize: "13px",
          timestampSize: "10px",
          badgeSize: "10px",
          badgePadding: "4px 8px",
          loaderSize: 56,
          preTradeContainerPadding: "10px 14px",
          preTradeCardPadding: "11px 13px",
          preTradeCardGap: "7px",
          preTradeCardMarginBottom: "9px",
          kickerSize: "10px",
        };
      case "comfortable":
        return {
          containerPadding: "12px",
          cardPadding: "14px",
          cardGap: "12px",
          cardMarginBottom: "12px",
          titleSize: "17px",
          descriptionSize: "15px",
          sourceSize: "16px",
          timestampSize: "13px",
          badgeSize: "12px",
          badgePadding: "6px 12px",
          loaderSize: 88,
          preTradeContainerPadding: "14px",
          preTradeCardPadding: "17px 23px",
          preTradeCardGap: "9px",
          preTradeCardMarginBottom: "13px",
          kickerSize: "11px",
        };
      default:
        return {
          containerPadding: "10px",
          cardPadding: "12px",
          cardGap: "10px",
          cardMarginBottom: "10px",
          titleSize: "16px",
          descriptionSize: "14px",
          sourceSize: "15px",
          timestampSize: "12px",
          badgeSize: "11px",
          badgePadding: "5px 10px",
          loaderSize: 72,
          preTradeContainerPadding: "12px 14px",
          preTradeCardPadding: "15px 19px",
          preTradeCardGap: "9px",
          preTradeCardMarginBottom: "11px",
          kickerSize: "11px",
        };
    }
  }, [tier]);

  const colors = {
    dark: theme.globallink.colors.Black[100],
    background: theme.globallink.colors.Grey[1100], // Dark background for the entire tile
    insightBackground: theme.globallink.colors.Slate[900], // Slightly lighter background for each insight box
    titleBackground: theme.globallink.colors.White[500],
    titleBackground2: "#43CFE6", // Background color for the title bar
    textPrimary: theme.globallink.colors.Blue[500], // Primary text color (white)
    textSecondary: theme.globallink.colors.Grey[600], // Secondary text color (light grey)
    border: theme.globallink.colors.Slate[700], // Border color (slightly darker grey)
  };

  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      padding: "10px",
      height: "100%",
      backgroundColor: colors.background,
      overflow: "auto",
      borderRadius: "8px",
    },
    insightContainer: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: colors.insightBackground,
      padding: "12px",
      marginBottom: "10px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      gap: "10px",
    },
    insightContainer1: {
      backgroundColor: colors.titleBackground,
      borderRadius: "8px",
      padding: "2px 0",
    },
    insightContainer2: {
      backgroundColor: colors.titleBackground2,
      color: colors.textPrimary,
      borderRadius: "8px",
      padding: "2px 0",
    },
    title: {
      color: colors.dark,
      fontSize: "16px",
      fontWeight: "bold",
      padding: "5px",
    },
    description: {
      color: colors.dark,
      fontSize: "14px",
      fontWeight: "normal",
      padding: "5px",
    },
    source: {
      color: colors.titleBackground,
      fontSize: "15px",
      fontWeight: "bold",
    },
    timestamp: {
      color: colors.textSecondary,
      fontSize: "12px",
      textAlign: "right",
    },
    preTradeHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      minWidth: 0,
    },
    preTradeKicker: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      minWidth: 0,
      color: "rgba(224,224,224,0.62)",
      fontWeight: 600,
      letterSpacing: "0.12em",
      lineHeight: "16px",
      textTransform: "uppercase",
    },
    preTradeTimestamp: {
      color: "rgba(224,224,224,0.52)",
      fontVariantNumeric: "tabular-nums",
      lineHeight: "16px",
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
    preTradeCountdownText: {
      color: "#BBDFFF",
      fontSize: "13px",
      fontWeight: 600,
      lineHeight: "18px",
    },
    preTradeCountdownValue: {
      color: "#FFFFFF",
      fontSize: "14px",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
    },
    preTradeContent: {
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      minWidth: 0,
    },
    preTradeTitle: {
      color: colors.titleBackground,
      display: "-webkit-box",
      fontWeight: 600,
      lineHeight: "1.35",
      letterSpacing: "-0.005em",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 2,
      overflow: "hidden",
    },
    preTradeDescription: {
      color: "rgba(224,224,224,0.72)",
      fontWeight: 400,
      lineHeight: "1.55",
      maxWidth: "80ch",
    },
    preTradeImpact: {
      display: "flex",
      alignItems: "baseline",
      gap: "8px",
      marginTop: "3px",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "16px",
    },
    preTradeImpactScore: {
      fontVariantNumeric: "tabular-nums",
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "8px",
      height: "100%",
    },
    loadingText: {
      color: colors.textSecondary,
      fontWeight: 600,
    },
  });

  const classes = useStyles();
  const internalStreamState = useInsightsWs({
    currencyPair,
    updateSpeed,
    userLanguage: localStorage.getItem("user_language") || "English",
    showCountDownInsights: true,
    enabled: !useStreamData,
  });
  const insights = useStreamData
    ? streamedInsights ?? []
    : internalStreamState.insights;
  const loading = useStreamData
    ? streamedLoading ?? false
    : internalStreamState.loading;

  return (
    <div
      className={classes.container}
      ref={containerRef}
      style={{
        padding: isPreTrade
          ? typography.preTradeContainerPadding
          : typography.containerPadding,
      }}
    >
      {loading || insights.length === 0 ? (
        <div className={classes.loadingContainer} role="status" aria-live="polite">
          <img
            src={loadingLogo}
            alt=""
            width={typography.loaderSize}
            height={typography.loaderSize}
            decoding="async"
          />
          <span
            className={classes.loadingText}
            style={{ fontSize: typography.descriptionSize }}
          >
            Generating insights...
          </span>
        </div>
      ) : (
        <>
          {insights.map((insight, index) => {
            const isCountdown = insight.type === "count_down";
            const isScheduledRelease =
              isCountdown ||
              (isPreTrade && isUpcomingReleaseTitle(insight.title));
            const countdown = isScheduledRelease
              ? getCountdownTime(insight.start_date, currentTimeMs)
              : null;
            const isUpcomingRelease =
              isScheduledRelease &&
              (!countdown?.isValid || !countdown.isPast);
            const impactTone =
              isScheduledRelease
                ? null
                : getImpactTone(insight.impact_score);
            const preTradeImpactTone =
              isPreTrade && impactTone
                ? getPreTradeSentimentTone(impactTone.label)
                : undefined;
            const metaText =
              isPreTrade && isScheduledRelease && countdown?.isPast ? (
                `Released ${formatDateTimeWithZone(
                  insight.start_date,
                  timeZone
                )}`
              ) : (
                <TimeSince timestamp={insight.timestamp} timeZone={timeZone} />
              );

            if (isPreTrade) {
              const preTradeCardStyle = isUpcomingRelease
                ? {
                    background:
                      "linear-gradient(135deg, rgba(50, 133, 224, 0.22), rgba(42, 91, 154, 0.08))",
                    border: "1px solid rgba(121, 185, 255, 0.5)",
                    boxShadow:
                      "inset 3px 0 0 #67B7FF, 0 4px 16px rgba(21, 95, 170, 0.12)",
                  }
                : {
                    background: `linear-gradient(90deg, ${
                      preTradeImpactTone?.surface || "rgba(255,255,255,0.035)"
                    } 0%, rgba(255,255,255,0.035) 42%)`,
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow: preTradeImpactTone
                      ? `inset 3px 0 0 ${preTradeImpactTone.accent}`
                      : "none",
                  };
              const iconKind: InsightIconKind = isUpcomingRelease
                ? "clock"
                : isScheduledRelease
                  ? "calendar"
                  : impactTone?.label === "Supportive"
                    ? "up"
                    : impactTone?.label === "Negative"
                      ? "down"
                      : "bars";
              const iconColor = isUpcomingRelease
                ? "#9DCCFF"
                : preTradeImpactTone?.accent || "rgba(224,224,224,0.56)";

              return (
                <div
                  key={getInsightKey(insight, index)}
                  className={classes.insightContainer}
                  style={{
                    ...preTradeCardStyle,
                    borderRadius: "10px",
                    padding: typography.preTradeCardPadding,
                    gap: typography.preTradeCardGap,
                    marginBottom: typography.preTradeCardMarginBottom,
                  }}
                >
                  <div className={classes.preTradeHeader}>
                    <div
                      className={classes.preTradeKicker}
                      style={{
                        fontSize: typography.kickerSize,
                        color: isUpcomingRelease ? "#9DCCFF" : undefined,
                      }}
                    >
                      <InsightIcon kind={iconKind} color={iconColor} />
                      <span>
                        {isUpcomingRelease
                          ? "Upcoming release"
                          : isScheduledRelease
                            ? "Economic data release"
                            : "Market update"}
                      </span>
                    </div>
                    <div
                      className={classes.preTradeTimestamp}
                      style={{ fontSize: typography.timestampSize }}
                    >
                      {metaText}
                    </div>
                  </div>
                  <div className={classes.preTradeContent}>
                    <div
                      className={classes.preTradeTitle}
                      style={{ fontSize: typography.titleSize }}
                    >
                      {insight.title || "Market update"}
                    </div>
                    {isUpcomingRelease && countdown?.isValid ? (
                      <div className={classes.preTradeCountdownText}>
                        Due in{" "}
                        <strong className={classes.preTradeCountdownValue}>
                          {countdown.compactTimeString}
                        </strong>
                      </div>
                    ) : null}
                    {isCountdown ? null : (
                      <div
                        className={classes.preTradeDescription}
                        style={{ fontSize: typography.descriptionSize }}
                      >
                        {insight.description || "No description available."}
                      </div>
                    )}
                    {impactTone && preTradeImpactTone ? (
                      <div
                        className={classes.preTradeImpact}
                        style={{ color: preTradeImpactTone.accent }}
                      >
                        <span>{getPreTradeImpactLabel(impactTone.label)}</span>
                        <span className={classes.preTradeImpactScore}>
                          {impactTone.scoreText}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            }

            const cardStyle = impactTone
              ? {
                  background: impactTone.background,
                  border: `1px solid ${impactTone.border}`,
                  boxShadow: `inset 4px 0 0 ${impactTone.accent}`,
                  padding: typography.cardPadding,
                  gap: typography.cardGap,
                  marginBottom: typography.cardMarginBottom,
                }
              : {
                  padding: typography.cardPadding,
                  gap: typography.cardGap,
                  marginBottom: typography.cardMarginBottom,
                };

            return (
              <div
                key={getInsightKey(insight, index)}
                className={classes.insightContainer}
                style={cardStyle}
              >
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div
                    className={classes.source}
                    style={{
                      color: impactTone ? impactTone.accent : undefined,
                      fontSize: typography.sourceSize,
                    }}
                  >
                    Update
                  </div>
                  <div
                    className="d-flex flex-column align-items-end"
                    style={{ gap: "6px" }}
                  >
                    <div
                      className={classes.timestamp}
                      style={{ fontSize: typography.timestampSize }}
                    >
                      {metaText}
                    </div>
                    {impactTone ? (
                      <div
                        style={{
                          borderRadius: "999px",
                          padding: typography.badgePadding,
                          fontSize: typography.badgeSize,
                          fontWeight: 700,
                          color: impactTone.accent,
                          background: impactTone.badgeBg,
                          border: `1px solid ${impactTone.badgeBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {impactTone.label} {impactTone.scoreText}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div
                  className={
                    isCountdown
                      ? classes.insightContainer2
                      : classes.insightContainer1
                  }
                  style={
                    impactTone
                      ? {
                          background: "rgba(255,255,255,0.72)",
                          border: `1px solid ${impactTone.badgeBorder}`,
                        }
                      : undefined
                  }
                >
                  <div
                    className={classes.title}
                    style={{ fontSize: typography.titleSize }}
                  >
                    {insight.title || "Market update"}
                  </div>
                  <div
                    className={classes.description}
                    style={{ fontSize: typography.descriptionSize }}
                  >
                    {isCountdown ? (
                      countdown?.isPast ? (
                        <>
                          <strong>{insight.title || "Market update"}</strong>{" "}
                          data was released at{" "}
                          <strong>
                            {formatDateTimeWithZone(
                              insight.start_date,
                              timeZone
                            )}
                          </strong>
                        </>
                      ) : (
                        <>
                          {"Data for "}
                          <strong>{insight.title || "Market update"}</strong>
                          {" to be released in "}
                          <strong>{countdown?.timeString}</strong>
                        </>
                      )
                    ) : (
                      <>{insight.description || "No description available."}</>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default CoTraderInsightsComponent;
const getCountdownTime = (startTime: any, now = Date.now()) => {
  const targetTime = parseUtcTimestampToMs(startTime);
  if (!Number.isFinite(targetTime)) {
    return {
      isValid: false,
      isPast: false,
      timeString: "-",
      compactTimeString: "-",
    };
  }

  const timeDiff = targetTime - now;
  const absDiff = Math.abs(timeDiff);
  const hours = Math.floor(
    (absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));

  const getTimeString = () => {
    if (hours > 0) {
      return `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${
        minutes === 1 ? "minute" : "minutes"
      }`;
    }

    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  };

  return {
    isValid: true,
    isPast: timeDiff <= 0,
    timeString: getTimeString(),
    compactTimeString:
      hours > 0 ? `${hours}h ${minutes}m` : minutes > 0 ? `${minutes}m` : "now",
  };
};
